// apps/web/src/lib/flashcardPrefs.ts
// ─────────────────────────────────────────────────────────────────────────
// FLASHCARD PREFERENCES + OVERRIDES — sync, localStorage-backed (web layer).
//
// The curriculum card (lesson.flashcards[n]) is the canonical default and is
// NEVER mutated. Per-user customization is stored as a separate override:
//
//   • isActive      → whether the card counts for the user (review + mastery)
//   • customBack     → user-edited answer text (front editing deferred to V2)
//
// Mastery policy that consumes these prefs lives in flashcardMastery.ts; this
// file is pure storage + the "effective card" projection. Kept out of
// @nisk/core so the engine stays pure (it never sees user prefs).
//
// TODO(post-migration): port to async Dexie reads, then delete the sync body.
// ─────────────────────────────────────────────────────────────────────────

import type { Flashcard } from '@nisk/core/learning';

// ── Storage keys ───────────────────────────────────────────────────────────
const PREF_PREFIX = 'nis.flashcardPref.';
const UNLOCK_PREFIX = 'nis.flashcardsUnlocked.';

// Same-tab change signal so open surfaces (Lesson, Flashcards) re-read prefs.
// Mirrors the convention in useProgress.ts / srs.ts.
export const FLASHCARD_PREF_EVENT = 'nis:flashcardPrefs';

function emitPrefChange(): void {
	try {
		window.dispatchEvent(new Event(FLASHCARD_PREF_EVENT));
	} catch {
		/* noop */
	}
}

// ── Preference shape ─────────────────────────────────────────────────────────
// One record per customized card. Absence of a record === all defaults
// (isActive: true, no custom text). We only persist records the user touched.
export interface UserFlashcardPreference {
	cardId: string;
	isActive: boolean;
	/** User-edited front. Reserved for V2; not surfaced in the V1 editor. */
	customFront?: string;
	/** User-edited answer/back text. */
	customBack?: string;
	updatedAt: string;
}

function prefKey(cardId: string): string {
	return `${PREF_PREFIX}${cardId}`;
}

// ── Read ─────────────────────────────────────────────────────────────────────
export function loadPreference(
	cardId: string,
): UserFlashcardPreference | undefined {
	try {
		const raw = localStorage.getItem(prefKey(cardId));
		if (!raw) return undefined;
		const parsed = JSON.parse(raw) as Partial<UserFlashcardPreference>;
		if (typeof parsed !== 'object' || parsed === null) return undefined;
		return {
			cardId,
			isActive: parsed.isActive !== false, // default active
			customFront:
				typeof parsed.customFront === 'string'
					? parsed.customFront
					: undefined,
			customBack:
				typeof parsed.customBack === 'string'
					? parsed.customBack
					: undefined,
			updatedAt:
				typeof parsed.updatedAt === 'string'
					? parsed.updatedAt
					: new Date().toISOString(),
		};
	} catch {
		return undefined;
	}
}

/** Build a cardId → preference map for a set of cards (one read each). */
export function loadPreferences(
	cardIds: string[],
): Record<string, UserFlashcardPreference | undefined> {
	const out: Record<string, UserFlashcardPreference | undefined> = {};
	for (const id of cardIds) out[id] = loadPreference(id);
	return out;
}

// ── Write ──────────────────────────────────────────────────────────────────
function savePreference(pref: UserFlashcardPreference): void {
	try {
		localStorage.setItem(prefKey(pref.cardId), JSON.stringify(pref));
		emitPrefChange();
	} catch {
		/* noop */
	}
}

/** Toggle (or set) whether a card is active. */
export function setCardActive(cardId: string, isActive: boolean): void {
	const existing = loadPreference(cardId);
	savePreference({
		cardId,
		isActive,
		customFront: existing?.customFront,
		customBack: existing?.customBack,
		updatedAt: new Date().toISOString(),
	});
}

/** Set (or clear) the custom answer text. Passing undefined/empty resets it. */
export function setCardCustomBack(
	cardId: string,
	customBack: string | undefined,
): void {
	const existing = loadPreference(cardId);
	const trimmed = customBack?.trim();
	savePreference({
		cardId,
		isActive: existing?.isActive !== false,
		customFront: existing?.customFront,
		customBack: trimmed && trimmed.length > 0 ? trimmed : undefined,
		updatedAt: new Date().toISOString(),
	});
}

// ── Effective card (curriculum default + override projection) ────────────────
export interface EffectiveFlashcard {
	id: string;
	front: string;
	back: string;
	isActive: boolean;
	/** True when the displayed text differs from the curriculum default. */
	isEdited: boolean;
	source: Flashcard;
}

export function getEffectiveCard(source: Flashcard): EffectiveFlashcard {
	const pref = loadPreference(source.id);
	const front = pref?.customFront ?? source.front;
	const back = pref?.customBack ?? source.back;
	return {
		id: source.id,
		front,
		back,
		isActive: pref?.isActive !== false,
		isEdited: Boolean(pref?.customFront || pref?.customBack),
		source,
	};
}

export function getEffectiveCards(sources: Flashcard[]): EffectiveFlashcard[] {
	return sources.map(getEffectiveCard);
}

// ── Unlock tracking (per lesson) ─────────────────────────────────────────────
// Flashcards unlock once the user completes the owning lesson. We record the
// unlock so the "Flashcards unlocked" moment fires exactly once per lesson.
function unlockKey(lessonId: string): string {
	return `${UNLOCK_PREFIX}${lessonId}`;
}

export function isLessonFlashcardsUnlocked(lessonId: string): boolean {
	try {
		return localStorage.getItem(unlockKey(lessonId)) === 'true';
	} catch {
		return false;
	}
}

/**
 * Marks a lesson's flashcards unlocked. Returns true ONLY on the transition
 * from locked → unlocked, so the caller can fire the one-time unlock moment.
 */
export function markLessonFlashcardsUnlocked(lessonId: string): boolean {
	try {
		if (isLessonFlashcardsUnlocked(lessonId)) return false;
		localStorage.setItem(unlockKey(lessonId), 'true');
		emitPrefChange();
		return true;
	} catch {
		return false;
	}
}
